// HoldingsOverview — the Holdings card below the summary bar: the holdings visualization
// (Bar/Treemap) across the full width of one library Surface. Source of truth:
// portfolio3.jsx .pf3-hero--body. Reads plain props; owns no data.
//
// It was a 58/42 grid — chart beside a column of stat cards — until those cards left:
// vs-the-market, weighted expense and equity exposure were removed (FM-2110), and the YTD
// figure moved into the chart's own header with its source breakdown beside it. What
// remains is the chart, so the card is the chart.
//
// The one thing this shell still decides is whether the plan's growth is credited rather
// than earned; the chart header labels the figure from it.
import PropTypes from 'prop-types';
import { Surface, Skeleton } from '../../../reusable';
import { isCreditedRate } from '../portfolio.benchmark';
import { HoldingsViz } from './HoldingsViz';
import './holdings-overview.css';

export function HoldingsOverview({
  funds = [], items, holdings, benchmark = null, plan, onPickFund, onPickModel, loading = false,
  // Forwarded to the chart: which view it opens on. The combined dashboard (FM-2406)
  // passes 'map'; the Portfolio page leaves the default 'bar'.
  defaultMode = 'bar',
  // The card's heading (FM-3036). The dashboard names this card like its other cards;
  // the Portfolio page passes none — its summary bar already introduces the chart.
  title,
}) {
  const heading = title ? <h2 className="holdings-overview__title">{title}</h2> : null;

  if (loading) {
    return (
      <Surface className="holdings-overview" padding="none">
        {heading}
        <div className="holdings-overview__grid" aria-busy="true">
          <div>
            <Skeleton variant="text" width={160} />
            <Skeleton variant="block" height={320} />
          </div>
        </div>
      </Surface>
    );
  }

  return (
    <Surface className="holdings-overview" padding="none">
      {heading}
      <div className="holdings-overview__grid">
        <HoldingsViz
          funds={funds}
          items={items}
          plan={plan}
          onPickFund={onPickFund}
          onPickModel={onPickModel}
          ytdGrowth={holdings?.ytdGrowth}
          credited={isCreditedRate(benchmark)}
          defaultMode={defaultMode}
        />
      </div>
    </Surface>
  );
}

HoldingsOverview.propTypes = {
  funds: PropTypes.array,
  /** Top-level holdings (models aggregated) — what the allocation charts plot. */
  items: PropTypes.array,
  /** Derived holdings stats — the chart header reads `ytdGrowth` off it. */
  holdings: PropTypes.object,
  /** The finished YTD benchmark (FM-2101) — says whether the growth was credited. */
  benchmark: PropTypes.object,
  /** Plan record — threaded to the chart tooltip's Future-election row. */
  plan: PropTypes.object,
  onPickFund: PropTypes.func,
  /** (modelKey) — a model bar/tile was clicked; the page opens the model allocation modal. */
  onPickModel: PropTypes.func,
  loading: PropTypes.bool,
  /** Which view the chart opens on: 'bar' (Portfolio) or 'map' (combined dashboard). */
  defaultMode: PropTypes.oneOf(['bar', 'map']),
  /** Card heading, already translated. Omitted on the Portfolio page (FM-3036). */
  title: PropTypes.string,
};

export default HoldingsOverview;
